/**
 * DatenBestaetigenStep — Schritt 7 des Kiosk-Wizards
 *
 * Vier Tap-to-Edit-Karten: Halter, Fahrzeug, Versicherung, Bank.
 * Tap → Modal mit Inline-Edit → Speichern → Modal schließt → Feld pulsiert grün.
 * Änderungen werden sofort in kiosk_sessions.bestaetigte_daten persistiert.
 */

import { useState, useCallback } from 'react';
import { User, Car, Shield, CreditCard, Pencil, Check, X, ChevronRight } from 'lucide-react';
import { toast } from 'sonner';

// ─── Types ────────────────────────────────────────────────────────────────────

export interface HalterFelder {
  name: string;
  anschrift: string;
  telefon: string;
  email: string;
}

export interface FahrzeugFelder {
  kennzeichen: string;
  fin: string;
  hsnTsn: string;
  erstzulassung: string;
}

export interface VersicherungFelder {
  eigeneVersicherung: string;
  vollkasko: boolean;
  gegnerVersicherung: string;
  vsNummer: string;
}

export interface BankFelder {
  iban: string;
  bic: string;
}

export interface KartenDaten {
  halter: HalterFelder;
  fahrzeug: FahrzeugFelder;
  versicherung: VersicherungFelder;
  bank: BankFelder;
}

export interface KioskFormInput {
  vorname: string;
  nachname: string;
  anschrift: string;
  telefon: string;
  email: string;
  kennzeichen: string;
  fin: string;
  hsn: string;
  tsn: string;
  erstzulassung: string;
  eigeneVersicherung: string;
  vollkasko: boolean;
  gegnerVersicherung: string;
  vsNummer: string;
  iban: string;
  bic: string;
  kioskSessionId: string;
}

interface Props {
  formData: KioskFormInput;
  onComplete: (daten: KartenDaten) => void;
  onBack: () => void;
}

const BRAND_BLUE = '#1E5FB7';
const BRAND_GREEN = '#3DB54A';

// ─── Pure mapping function (exported for unit tests) ─────────────────────────

export function mapFormDataToKarten(formData: KioskFormInput): KartenDaten {
  const name = [formData.vorname, formData.nachname].filter(Boolean).join(' ');
  const hsnTsn = formData.hsn && formData.tsn ? `${formData.hsn}/${formData.tsn}` : '';

  return {
    halter: {
      name,
      anschrift: formData.anschrift,
      telefon: formData.telefon,
      email: formData.email,
    },
    fahrzeug: {
      kennzeichen: formData.kennzeichen,
      fin: formData.fin,
      hsnTsn,
      erstzulassung: formData.erstzulassung,
    },
    versicherung: {
      eigeneVersicherung: formData.eigeneVersicherung,
      vollkasko: formData.vollkasko,
      gegnerVersicherung: formData.gegnerVersicherung,
      vsNummer: formData.vsNummer,
    },
    bank: {
      iban: formData.iban,
      bic: formData.bic,
    },
  };
}

async function persistDaten(sessionId: string, daten: KartenDaten): Promise<void> {
  if (!sessionId) return;

  const payload = {
    halter: daten.halter,
    fahrzeug: daten.fahrzeug,
    versicherung: daten.versicherung,
    bank: daten.bank,
  };

  const resp = await fetch('/api/kiosk/update-daten', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ session_id: sessionId, daten: payload }),
  });

  if (!resp.ok) {
    const err = await resp.json().catch(() => ({}));
    throw new Error(err.error || 'Speichern fehlgeschlagen');
  }
}

// ─── Field row inside modal ───────────────────────────────────────────────────

function ModalField({
  label,
  value,
  onChange,
  type = 'text',
  placeholder,
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  type?: string;
  placeholder?: string;
}) {
  return (
    <div>
      <label className="block text-[12px] font-semibold text-gray-500 mb-1">{label}</label>
      <input
        type={type}
        value={value}
        onChange={e => onChange(e.target.value)}
        placeholder={placeholder}
        className="w-full px-4 py-3 rounded-xl border-2 border-gray-200 bg-white text-[15px] focus:outline-none focus:ring-2 focus:ring-[#1E5FB7]/30 focus:border-[#1E5FB7] transition-all"
        style={{ minHeight: 48 }}
      />
    </div>
  );
}

// ─── Edit Modal ───────────────────────────────────────────────────────────────

function EditModal({
  title,
  icon,
  children,
  onSave,
  onClose,
}: {
  title: string;
  icon: React.ReactNode;
  children: React.ReactNode;
  onSave: () => void;
  onClose: () => void;
}) {
  return (
    <div className="fixed inset-0 z-[200] flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" onClick={onClose} />
      <div className="relative w-full max-w-md bg-white rounded-3xl shadow-2xl overflow-hidden">
        <div className="flex items-center justify-between px-6 py-5 border-b border-gray-100">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-2xl flex items-center justify-center" style={{ background: BRAND_BLUE }}>
              {icon}
            </div>
            <h3 className="text-[17px] font-bold text-gray-900">{title} bearbeiten</h3>
          </div>
          <button
            onClick={onClose}
            className="w-9 h-9 rounded-xl hover:bg-gray-100 flex items-center justify-center transition-colors"
          >
            <X size={18} className="text-gray-400" />
          </button>
        </div>

        <div className="px-6 py-5 space-y-4 max-h-[60vh] overflow-y-auto">
          {children}
        </div>

        <div className="px-6 pb-6">
          <button
            onClick={onSave}
            className="w-full py-4 rounded-2xl text-[15px] font-bold text-white shadow-md hover:opacity-90 transition-all flex items-center justify-center gap-2"
            style={{ background: `linear-gradient(135deg, ${BRAND_BLUE}, #1A4F9A)`, minHeight: 56 }}
          >
            <Check size={18} />
            Speichern
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── Data card row ────────────────────────────────────────────────────────────

function CardRow({ label, value, pulsing }: { label: string; value: string; pulsing: boolean }) {
  return (
    <div className="flex items-start gap-3 py-1.5">
      <span className="text-[12px] text-gray-400 w-28 flex-shrink-0 pt-0.5">{label}</span>
      <span
        className={`text-[13px] font-medium flex-1 transition-colors ${pulsing ? 'text-emerald-600' : 'text-gray-800'} ${pulsing ? 'animate-pulse-once' : ''}`}
      >
        {value || <span className="text-gray-300 italic">—</span>}
      </span>
    </div>
  );
}

// ─── Tap-to-Edit card ─────────────────────────────────────────────────────────

function EditCard({
  icon,
  title,
  rows,
  onEdit,
}: {
  icon: React.ReactNode;
  title: string;
  rows: { label: string; value: string; pulsing: boolean }[];
  onEdit: () => void;
}) {
  return (
    <button
      onClick={onEdit}
      className="w-full text-left bg-white rounded-2xl border-2 border-gray-100 shadow-sm hover:border-[#1E5FB7]/30 hover:shadow-md transition-all active:scale-[0.99] p-5"
      style={{ minHeight: 100 }}
    >
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-3">
          <div className="w-9 h-9 rounded-xl flex items-center justify-center flex-shrink-0" style={{ background: BRAND_BLUE }}>
            {icon}
          </div>
          <span className="text-[14px] font-bold text-gray-900">{title}</span>
        </div>
        <div className="flex items-center gap-1.5 text-[12px] text-[#1E5FB7] font-semibold">
          <Pencil size={13} />
          <span>Tippen zum Bearbeiten</span>
          <ChevronRight size={14} />
        </div>
      </div>
      <div className="divide-y divide-gray-50">
        {rows.map(r => (
          <CardRow key={r.label} label={r.label} value={r.value} pulsing={r.pulsing} />
        ))}
      </div>
    </button>
  );
}

// ─── Main component ───────────────────────────────────────────────────────────

type ModalType = 'halter' | 'fahrzeug' | 'versicherung' | 'bank' | null;

export function DatenBestaetigenStep({ formData, onComplete, onBack }: Props) {
  const [daten, setDaten] = useState<KartenDaten>(() => mapFormDataToKarten(formData));
  const [modal, setModal] = useState<ModalType>(null);
  const [pulsingFields, setPulsingFields] = useState<Set<string>>(new Set());

  // Temporary edit state per modal
  const [editHalter, setEditHalter] = useState<HalterFelder>(daten.halter);
  const [editFahrzeug, setEditFahrzeug] = useState<FahrzeugFelder>(daten.fahrzeug);
  const [editVersicherung, setEditVersicherung] = useState<VersicherungFelder>(daten.versicherung);
  const [editBank, setEditBank] = useState<BankFelder>(daten.bank);

  const openModal = (type: ModalType) => {
    if (type === 'halter') setEditHalter(daten.halter);
    if (type === 'fahrzeug') setEditFahrzeug(daten.fahrzeug);
    if (type === 'versicherung') setEditVersicherung(daten.versicherung);
    if (type === 'bank') setEditBank(daten.bank);
    setModal(type);
  };

  const pulseFields = useCallback((fields: string[]) => {
    setPulsingFields(new Set(fields));
    setTimeout(() => setPulsingFields(new Set()), 1800);
  }, []);

  const saveModal = async (type: ModalType) => {
    let updated = { ...daten };
    let changedFields: string[] = [];

    if (type === 'halter') {
      const changed = Object.keys(editHalter).filter(
        k => editHalter[k as keyof HalterFelder] !== daten.halter[k as keyof HalterFelder],
      );
      updated = { ...updated, halter: editHalter };
      changedFields = changed.map(k => `halter.${k}`);
    } else if (type === 'fahrzeug') {
      const changed = Object.keys(editFahrzeug).filter(
        k => editFahrzeug[k as keyof FahrzeugFelder] !== daten.fahrzeug[k as keyof FahrzeugFelder],
      );
      updated = { ...updated, fahrzeug: editFahrzeug };
      changedFields = changed.map(k => `fahrzeug.${k}`);
    } else if (type === 'versicherung') {
      const changed = Object.keys(editVersicherung).filter(
        k => String(editVersicherung[k as keyof VersicherungFelder]) !== String(daten.versicherung[k as keyof VersicherungFelder]),
      );
      updated = { ...updated, versicherung: editVersicherung };
      changedFields = changed.map(k => `versicherung.${k}`);
    } else if (type === 'bank') {
      const changed = Object.keys(editBank).filter(
        k => editBank[k as keyof BankFelder] !== daten.bank[k as keyof BankFelder],
      );
      updated = { ...updated, bank: editBank };
      changedFields = changed.map(k => `bank.${k}`);
    }

    setDaten(updated);
    setModal(null);

    if (changedFields.length > 0) {
      pulseFields(changedFields);
      try {
        await persistDaten(formData.kioskSessionId, updated);
      } catch (e) {
        toast.error('Speichern fehlgeschlagen', { description: 'Bitte versuchen Sie es erneut.' });
      }
    }
  };

  const handleWeiter = async () => {
    try {
      await persistDaten(formData.kioskSessionId, daten);
    } catch {
      // Non-blocking — user can still proceed
    }
    onComplete(daten);
  };

  const isPulsing = (field: string) => pulsingFields.has(field);

  return (
    <>
      <div className="space-y-5">
        <div className="text-center mb-2">
          <h2 className="text-[24px] font-bold text-gray-900">Stimmt alles?</h2>
          <p className="text-[14px] text-gray-500 mt-1">Tippen Sie auf eine Karte zum Korrigieren</p>
        </div>

        {/* Halter */}
        <EditCard
          icon={<User size={18} className="text-white" />}
          title="👤 Halter"
          rows={[
            { label: 'Name', value: daten.halter.name, pulsing: isPulsing('halter.name') },
            { label: 'Anschrift', value: daten.halter.anschrift, pulsing: isPulsing('halter.anschrift') },
            { label: 'Telefon', value: daten.halter.telefon, pulsing: isPulsing('halter.telefon') },
            { label: 'E-Mail', value: daten.halter.email, pulsing: isPulsing('halter.email') },
          ]}
          onEdit={() => openModal('halter')}
        />

        {/* Fahrzeug */}
        <EditCard
          icon={<Car size={18} className="text-white" />}
          title="🚗 Fahrzeug"
          rows={[
            { label: 'Kennzeichen', value: daten.fahrzeug.kennzeichen, pulsing: isPulsing('fahrzeug.kennzeichen') },
            { label: 'FIN', value: daten.fahrzeug.fin, pulsing: isPulsing('fahrzeug.fin') },
            { label: 'HSN/TSN', value: daten.fahrzeug.hsnTsn, pulsing: isPulsing('fahrzeug.hsnTsn') },
            { label: 'Erstzulassung', value: daten.fahrzeug.erstzulassung, pulsing: isPulsing('fahrzeug.erstzulassung') },
          ]}
          onEdit={() => openModal('fahrzeug')}
        />

        {/* Versicherung */}
        <EditCard
          icon={<Shield size={18} className="text-white" />}
          title="🛡 Versicherung"
          rows={[
            { label: 'Eigene Vers.', value: daten.versicherung.eigeneVersicherung, pulsing: isPulsing('versicherung.eigeneVersicherung') },
            { label: 'Vollkasko', value: daten.versicherung.vollkasko ? 'Ja' : 'Nein', pulsing: isPulsing('versicherung.vollkasko') },
            { label: 'Gegner Vers.', value: daten.versicherung.gegnerVersicherung, pulsing: isPulsing('versicherung.gegnerVersicherung') },
            { label: 'VS-Nummer', value: daten.versicherung.vsNummer, pulsing: isPulsing('versicherung.vsNummer') },
          ]}
          onEdit={() => openModal('versicherung')}
        />

        {/* Bank */}
        <EditCard
          icon={<CreditCard size={18} className="text-white" />}
          title="📄 Bank"
          rows={[
            { label: 'IBAN', value: daten.bank.iban, pulsing: isPulsing('bank.iban') },
            { label: 'BIC', value: daten.bank.bic, pulsing: isPulsing('bank.bic') },
          ]}
          onEdit={() => openModal('bank')}
        />

        <div className="flex items-center justify-between pt-2">
          <button
            onClick={onBack}
            className="flex items-center gap-2 px-6 py-3 rounded-xl border-2 border-gray-200 hover:bg-gray-50 transition-all text-[14px] font-semibold text-gray-600"
            style={{ minHeight: 52 }}
          >
            Zurück
          </button>
          <button
            onClick={handleWeiter}
            className="flex items-center gap-2 px-7 py-3 rounded-xl text-[14px] font-bold text-white shadow-md hover:opacity-90 transition-all"
            style={{ background: `linear-gradient(135deg, ${BRAND_BLUE}, #1A4F9A)`, minHeight: 52 }}
          >
            Alles korrekt — Weiter
          </button>
        </div>
      </div>

      {/* Halter Modal */}
      {modal === 'halter' && (
        <EditModal
          title="Halter"
          icon={<User size={16} className="text-white" />}
          onSave={() => saveModal('halter')}
          onClose={() => setModal(null)}
        >
          <ModalField
            label="Name"
            value={editHalter.name}
            onChange={v => setEditHalter(p => ({ ...p, name: v }))}
            placeholder="Max Mustermann"
          />
          <ModalField
            label="Anschrift"
            value={editHalter.anschrift}
            onChange={v => setEditHalter(p => ({ ...p, anschrift: v }))}
            placeholder="Hauptstr. 1, 12345 Musterstadt"
          />
          <ModalField
            label="Telefon"
            value={editHalter.telefon}
            onChange={v => setEditHalter(p => ({ ...p, telefon: v }))}
            type="tel"
            placeholder="0151 12345678"
          />
          <ModalField
            label="E-Mail"
            value={editHalter.email}
            onChange={v => setEditHalter(p => ({ ...p, email: v }))}
            type="email"
            placeholder="max@example.de"
          />
        </EditModal>
      )}

      {/* Fahrzeug Modal */}
      {modal === 'fahrzeug' && (
        <EditModal
          title="Fahrzeug"
          icon={<Car size={16} className="text-white" />}
          onSave={() => saveModal('fahrzeug')}
          onClose={() => setModal(null)}
        >
          <ModalField
            label="Kennzeichen"
            value={editFahrzeug.kennzeichen}
            onChange={v => setEditFahrzeug(p => ({ ...p, kennzeichen: v.toUpperCase() }))}
            placeholder="MK-AB 123"
          />
          <ModalField
            label="FIN (Fahrzeugidentifikationsnummer)"
            value={editFahrzeug.fin}
            onChange={v => setEditFahrzeug(p => ({ ...p, fin: v.toUpperCase() }))}
            placeholder="WBA3A9G50ENP25985"
          />
          <ModalField
            label="HSN/TSN"
            value={editFahrzeug.hsnTsn}
            onChange={v => setEditFahrzeug(p => ({ ...p, hsnTsn: v }))}
            placeholder="0005/ACE"
          />
          <ModalField
            label="Erstzulassung"
            value={editFahrzeug.erstzulassung}
            onChange={v => setEditFahrzeug(p => ({ ...p, erstzulassung: v }))}
            placeholder="2021-03-15"
          />
        </EditModal>
      )}

      {/* Versicherung Modal */}
      {modal === 'versicherung' && (
        <EditModal
          title="Versicherung"
          icon={<Shield size={16} className="text-white" />}
          onSave={() => saveModal('versicherung')}
          onClose={() => setModal(null)}
        >
          <ModalField
            label="Eigene Versicherung (z. B. HUK)"
            value={editVersicherung.eigeneVersicherung}
            onChange={v => setEditVersicherung(p => ({ ...p, eigeneVersicherung: v }))}
            placeholder="HUK24, ADAC Versicherung, ..."
          />
          <div>
            <label className="block text-[12px] font-semibold text-gray-500 mb-2">Vollkasko</label>
            <div className="flex gap-3">
              {[true, false].map(val => (
                <button
                  key={String(val)}
                  onClick={() => setEditVersicherung(p => ({ ...p, vollkasko: val }))}
                  className="flex-1 py-3 rounded-xl border-2 text-[14px] font-semibold transition-all"
                  style={{
                    borderColor: editVersicherung.vollkasko === val ? BRAND_BLUE : '#E5E7EB',
                    background: editVersicherung.vollkasko === val ? '#EFF6FF' : '#F9FAFB',
                    color: editVersicherung.vollkasko === val ? BRAND_BLUE : '#6B7280',
                    minHeight: 48,
                  }}
                >
                  {val ? 'Ja' : 'Nein'}
                </button>
              ))}
            </div>
          </div>
          <ModalField
            label="Gegnerische Versicherung"
            value={editVersicherung.gegnerVersicherung}
            onChange={v => setEditVersicherung(p => ({ ...p, gegnerVersicherung: v }))}
            placeholder="Allianz, HUK, ..."
          />
          <ModalField
            label="Versicherungsschein-Nummer"
            value={editVersicherung.vsNummer}
            onChange={v => setEditVersicherung(p => ({ ...p, vsNummer: v }))}
            placeholder="VS-123456789"
          />
        </EditModal>
      )}

      {/* Bank Modal */}
      {modal === 'bank' && (
        <EditModal
          title="Bank"
          icon={<CreditCard size={16} className="text-white" />}
          onSave={() => saveModal('bank')}
          onClose={() => setModal(null)}
        >
          <div className="bg-blue-50 border border-blue-200 rounded-xl px-4 py-3 flex items-start gap-2 mb-1">
            <Shield size={14} className="text-blue-500 mt-0.5 flex-shrink-0" />
            <p className="text-[12px] text-blue-700 leading-relaxed">
              Nur IBAN für eventuelle Rückerstattungen — keine PIN oder CVV.
            </p>
          </div>
          <ModalField
            label="IBAN"
            value={editBank.iban}
            onChange={v => setEditBank(p => ({ ...p, iban: v.replace(/\s/g, '').toUpperCase() }))}
            placeholder="DE89370400440532013000"
          />
          <ModalField
            label="BIC (optional)"
            value={editBank.bic}
            onChange={v => setEditBank(p => ({ ...p, bic: v.toUpperCase() }))}
            placeholder="COBADEFFXXX"
          />
        </EditModal>
      )}

      <style>{`
        @keyframes pulse-once {
          0%   { color: #059669; }
          50%  { color: #10b981; }
          100% { color: #059669; }
        }
        .animate-pulse-once {
          animation: pulse-once 1.5s ease-in-out;
          color: #059669;
        }
      `}</style>
    </>
  );
}
